import { useCallback, useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
    MAX_COLS,
    readAttrs,
    extractCells,
    getCaptionsEnabled,
    emptyCell
} from './imageGridShared';
import { useImageGridActions } from './useImageGridActions';
import { GridToolbar } from './GridToolbar';
import { GridCell } from './GridCell';

// ── ImageGridView (root NodeView) ─────────────────────────────────────────────

/**
 * A read-only single-row grid centres its photos instead of pinning them left.
 * The left-pinning rules live in each app's index.css - which this package does
 * not own - so they are defeated with `!important` from here rather than edited
 * there. Literal tokens, so every consumer's Tailwind scanner emits them.
 */
const CENTERED_ROW_CLASS =
    ' image-grid--single-row [&_.image-grid-cell.is-filled]:!justify-center [&_.image-grid-cell_img]:!object-center';

export function ImageGridView({ node, selected, editor, getPos }: NodeViewProps) {
    const attrs = readAttrs(node);
    const cells = extractCells(node);
    const editable = editor.isEditable;
    // One picture, or one row of them, reads as a centred figure rather than a
    // left-aligned strip.
    const centerRow = !editable && attrs.rows === 1;
    const showCaptions = getCaptionsEnabled(editor);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);

    useEffect(() => {
        if (!active) return;
        function onDocMouseDown(e: MouseEvent) {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(e.target as Node)) {
                setActive(false);
            }
        }
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [active]);

    const {
        handleCellRemoved,
        handleCellCaption,
        handleColResize,
        uploadAndFill,
        handleDropFile,
        buildCellDragPayload,
        handleMoveCell,
        handleAddRow,
        handleRemoveRow,
        handleAddCol,
        handleRemoveCol,
        handleHeightChange,
        handleDeleteGrid,
        handlePreset
    } = useImageGridActions({ editor, getPos, node, attrs, cells });

    const handlePick = useCallback(
        (cellIndex: number) => {
            if (!fileInputRef.current) return;
            const input = fileInputRef.current;
            const onChange = async (e: Event) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                (e.target as HTMLInputElement).value = '';
                if (!file) return;
                await uploadAndFill(cellIndex, file);
            };
            input.addEventListener('change', onChange, { once: true });
            input.click();
        },
        [uploadAndFill]
    );

    return (
        <NodeViewWrapper>
            <div
                ref={wrapperRef}
                className="image-grid-wrapper"
                style={{ position: 'relative' }}
                onClick={editable ? () => setActive(true) : undefined}
            >
                {editable && (selected || active) && (
                    <GridToolbar
                        attrs={attrs}
                        onAddRow={handleAddRow}
                        onRemoveRow={handleRemoveRow}
                        onAddCol={handleAddCol}
                        onRemoveCol={handleRemoveCol}
                        onHeightChange={handleHeightChange}
                        onPreset={handlePreset}
                        onDeleteGrid={handleDeleteGrid}
                    />
                )}
                <div
                    className={`image-grid${editable && (selected || active) ? ' is-selected' : ''}${editable ? '' : ' image-grid--readonly'}${centerRow ? CENTERED_ROW_CLASS : ''}`}
                    data-image-grid-container=""
                    style={{
                        display: 'grid',
                        gridTemplateColumns: attrs.colWidths.map((w) => `${w}fr`).join(' '),
                        gridAutoRows: `${attrs.rowHeight}px`,
                        gap: 4
                    }}
                >
                    {Array.from({ length: attrs.rows }, (_, r) =>
                        Array.from({ length: attrs.cols }, (__, c) => {
                            const cellIndex = r * attrs.cols + c;
                            return (
                                <GridCell
                                    key={cellIndex}
                                    cell={cells[cellIndex] ?? emptyCell()}
                                    cellIndex={cellIndex}
                                    colIndex={c}
                                    colWidths={attrs.colWidths}
                                    isLastCol={c === attrs.cols - 1}
                                    editable={editable}
                                    showCaptions={showCaptions}
                                    onRemoved={handleCellRemoved}
                                    onResize={handleColResize}
                                    onPick={handlePick}
                                    onDropFile={handleDropFile}
                                    onDragStartCell={buildCellDragPayload}
                                    onMoveCell={handleMoveCell}
                                    onCaptionChange={handleCellCaption}
                                />
                            );
                        })
                    )}
                </div>
                {editable && (
                    <>
                        <button
                            type="button"
                            className="image-grid-edge-btn image-grid-edge-btn--bottom"
                            title="Add row"
                            onClick={handleAddRow}
                        >
                            <PlusIcon width={12} height={12} />
                        </button>
                        <button
                            type="button"
                            className="image-grid-edge-btn image-grid-edge-btn--right"
                            title="Add column"
                            onClick={handleAddCol}
                            disabled={attrs.cols >= MAX_COLS}
                        >
                            <PlusIcon width={12} height={12} />
                        </button>
                    </>
                )}
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
            />
        </NodeViewWrapper>
    );
}
